// MARK://dhk8/admin-shell/components/EntityCombobox
// Typeahead genérico PRESENTACIONAL para elegir UNA entidad (Gustavo 2026-06-22).
// El padre provee `fetchOptions(query)` (async); este componente no conoce el
// transporte (REST/Graph/lo que sea). Input + dropdown filtrable con debounce,
// navegación por teclado (↑↓ / Enter / Esc) y muestra la opción seleccionada.
// Self-contained: sin libs externas nuevas. Estilos `nui-*` en <style>.
//
// `value` es el objeto seleccionado (o null). Al elegir, se emite el objeto vía
// onChange; con allowClear se puede vaciar a null. getOptionValue da la key
// estable; getOptionLabel da el texto que se muestra en el input al seleccionar.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface EntityComboboxProps<T> {
  value: T | null
  onChange: (value: T | null) => void
  fetchOptions: (query: string) => Promise<T[]>
  renderOption: (option: T) => ReactNode
  getOptionValue: (option: T) => string
  getOptionLabel: (option: T) => string
  placeholder?: string
  allowClear?: boolean
  /** Debounce de la búsqueda en ms (default 300). */
  debounceMs?: number
  disabled?: boolean
}

export default function EntityCombobox<T>({
  value,
  onChange,
  fetchOptions,
  renderOption,
  getOptionValue,
  getOptionLabel,
  placeholder = 'Buscar…',
  allowClear,
  debounceMs = 300,
  disabled,
}: EntityComboboxProps<T>) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(-1)

  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reqSeq = useRef(0)
  const listId = useId()
  // El dropdown se renderiza en un portal a <body> con posición FIJA calculada del
  // control, para que NO lo recorte el overflow del modal/hoja contenedora.
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null)

  const selectedLabel = value ? getOptionLabel(value) : ''

  // Dispara fetch debounced cuando hay dropdown abierto y cambia la query.
  useEffect(() => {
    if (!open) return
    if (timer.current) clearTimeout(timer.current)
    setLoading(true)
    timer.current = setTimeout(() => {
      const seq = ++reqSeq.current
      Promise.resolve(fetchOptions(query))
        .then((res) => {
          if (seq !== reqSeq.current) return // descarta respuestas viejas (race)
          setOptions(res)
          setActive(res.length ? 0 : -1)
        })
        .catch(() => {
          if (seq !== reqSeq.current) return
          setOptions([])
          setActive(-1)
        })
        .finally(() => {
          if (seq === reqSeq.current) setLoading(false)
        })
    }, debounceMs)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open])

  // Cierra al hacer click fuera (ignora el control Y la lista portada).
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      const inRoot = rootRef.current?.contains(t)
      const inList = listRef.current?.contains(t)
      if (!inRoot && !inList) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Posiciona el dropdown bajo el control (fixed) y lo reubica en scroll/resize.
  useEffect(() => {
    if (!open) { setMenuRect(null); return }
    const place = () => {
      const r = rootRef.current?.getBoundingClientRect()
      if (r) setMenuRect({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open])

  const openDropdown = () => {
    if (disabled || open) return
    setOpen(true)
    setQuery('')
  }

  const choose = (opt: T) => {
    onChange(opt)
    setOpen(false)
    setQuery('')
    inputRef.current?.blur()
  }

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        openDropdown()
        return
      }
      setActive((a) => (options.length ? (a + 1) % options.length : -1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) return
      setActive((a) => (options.length ? (a - 1 + options.length) % options.length : -1))
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && active < options.length) {
        e.preventDefault()
        choose(options[active])
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault()
        setOpen(false)
        setQuery('')
      }
    }
  }

  // Lo que se ve en el input: mientras está abierto, lo que el usuario teclea;
  // cerrado, la etiqueta de la selección.
  const inputValue = open ? query : selectedLabel

  return (
    <div className="nui-combo" ref={rootRef}>
      <style>{NUI_COMBO_CSS}</style>

      <div className={'nui-combo__control' + (disabled ? ' nui-combo__control--disabled' : '')}>
        <input
          ref={inputRef}
          type="text"
          className="nui-combo__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${listId}-opt-${active}` : undefined}
          autoComplete="off"
          disabled={disabled}
          placeholder={selectedLabel && !open ? selectedLabel : placeholder}
          value={inputValue}
          onChange={(e) => {
            if (!open) setOpen(true)
            setQuery(e.target.value)
          }}
          onFocus={openDropdown}
          onClick={openDropdown}
          onKeyDown={onKeyDown}
        />
        {allowClear && value && !disabled && (
          <button
            type="button"
            className="nui-combo__clear"
            onClick={clear}
            aria-label="Limpiar selección"
            tabIndex={-1}
          >
            ✕
          </button>
        )}
        <span className="nui-combo__chevron" aria-hidden="true">▾</span>
      </div>

      {open && menuRect && createPortal(
        <ul className="nui-combo__list" ref={listRef} id={listId} role="listbox" style={{ position: 'fixed', top: menuRect.top, left: menuRect.left, width: menuRect.width, zIndex: 9999 }}>
          {loading ? (
            <li className="nui-combo__msg" aria-disabled="true">Buscando…</li>
          ) : options.length === 0 ? (
            <li className="nui-combo__msg" aria-disabled="true">Sin coincidencias</li>
          ) : (
            options.map((opt, i) => {
              const selected = value != null && getOptionValue(opt) === getOptionValue(value)
              return (
                <li
                  key={getOptionValue(opt)}
                  id={`${listId}-opt-${i}`}
                  role="option"
                  aria-selected={selected}
                  className={
                    'nui-combo__opt' +
                    (i === active ? ' nui-combo__opt--active' : '') +
                    (selected ? ' nui-combo__opt--selected' : '')
                  }
                  onMouseEnter={() => setActive(i)}
                  // onMouseDown (no onClick) para no perder el foco antes de elegir.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    choose(opt)
                  }}
                >
                  {renderOption(opt)}
                </li>
              )
            })
          )}
        </ul>,
        document.body,
      )}
    </div>
  )
}

const NUI_COMBO_CSS = `
.nui-combo { position: relative; width: 100%; }
.nui-combo__control {
  position: relative; display: flex; align-items: center;
}
.nui-combo__input {
  width: 100%; min-height: 44px;
  padding: 0 calc(var(--pos-space-3) + 18px) 0 var(--pos-space-3);
  border: 1px solid var(--pos-separator); border-radius: var(--pos-radius-md);
  background: var(--pos-canvas); color: var(--pos-fg);
  font-family: var(--font-sans); font-size: var(--pos-text-sm);
}
.nui-combo__input:focus {
  outline: none; border-color: var(--pos-tint);
  box-shadow: 0 0 0 3px var(--pos-tint-soft);
}
.nui-combo__control--disabled .nui-combo__input { opacity: .5; cursor: not-allowed; }
.nui-combo__clear {
  position: absolute; right: calc(var(--pos-space-3) + 14px);
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; padding: 0;
  border: none; background: transparent; cursor: pointer;
  color: var(--pos-fg-tertiary); font-size: 11px; line-height: 1;
  border-radius: var(--pos-radius-pill);
}
.nui-combo__clear:hover { color: var(--pos-fg); background: var(--pos-elevated); }
.nui-combo__chevron {
  position: absolute; right: var(--pos-space-3);
  color: var(--pos-fg-tertiary); font-size: 10px; pointer-events: none; line-height: 1;
}
.nui-combo__list {
  /* posición fija + top/left/width vienen inline (portal a <body>); aquí solo estilo */
  z-index: 9999;
  margin: 0; padding: var(--pos-space-1); list-style: none;
  max-height: 280px; overflow-y: auto;
  background: var(--pos-surface);
  border: 1px solid var(--pos-separator-strong);
  border-radius: var(--pos-radius-md);
  box-shadow: var(--pos-shadow-3);
}
.nui-combo__opt {
  padding: var(--pos-space-2) var(--pos-space-3);
  border-radius: var(--pos-radius-sm);
  font-size: var(--pos-text-sm); color: var(--pos-fg);
  cursor: pointer;
  transition: background var(--pos-duration-fast) var(--pos-ease-out);
}
.nui-combo__opt--active { background: var(--pos-elevated); }
.nui-combo__opt--selected { color: var(--pos-tint-on); background: var(--pos-tint-soft); font-weight: var(--pos-weight-medium); }
.nui-combo__opt--selected.nui-combo__opt--active { background: var(--pos-tint-hover); }
.nui-combo__msg {
  padding: var(--pos-space-3);
  font-size: var(--pos-text-sm); color: var(--pos-fg-tertiary);
  text-align: center;
}
`
